import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
  API_AUTH_REQUIRED_EVENT,
  API_CONFIG_CHANGED_EVENT,
  getDefaultApiUrl,
  getRuntimeApiConfig,
  getStoredApiUrl,
  isAllowedApiUrl,
  saveRuntimeApiConfig,
} from '../api/config';
import { BILLABLE_LOCK_EVENT } from '../hooks/useBillableNavigationLock';
import { SessionActionProvider } from './SessionActionContext';

/** Strip the scheme so the connected Worker host reads cleanly in the sidebar. */
function hostLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

interface TokenGateProps {
  children: ReactNode;
}

/**
 * Legacy runtime shared-token gate. Used when Cognito SSO is not configured.
 */
function TokenGate({ children }: TokenGateProps) {
  const initialConfig = getRuntimeApiConfig();
  const [configured, setConfigured] = useState(Boolean(initialConfig));
  const [editing, setEditing] = useState(false);
  const [apiUrl, setApiUrl] = useState(initialConfig?.apiUrl ?? getDefaultApiUrl());
  const [token, setToken] = useState('');
  const [validationError, setValidationError] = useState('');
  const [billableLocked, setBillableLocked] = useState(false);

  useEffect(() => {
    const handleAuthRequired = () => {
      const current = getRuntimeApiConfig();
      setConfigured(false);
      setEditing(false);
      setApiUrl(current?.apiUrl ?? getStoredApiUrl());
      setToken('');
      setValidationError('The Worker rejected this session. Enter a valid runtime token.');
    };
    const handleConfigChanged = () => setConfigured(Boolean(getRuntimeApiConfig()));
    const handleBillableLock = (event: Event) => {
      setBillableLocked(Boolean((event as CustomEvent<boolean>).detail));
    };

    window.addEventListener(API_AUTH_REQUIRED_EVENT, handleAuthRequired);
    window.addEventListener(API_CONFIG_CHANGED_EVENT, handleConfigChanged);
    window.addEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
    return () => {
      window.removeEventListener(API_AUTH_REQUIRED_EVENT, handleAuthRequired);
      window.removeEventListener(API_CONFIG_CHANGED_EVENT, handleConfigChanged);
      window.removeEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
    };
  }, []);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalizedUrl = apiUrl.trim().replace(/\/+$/, '');
    const normalizedToken = token.trim();

    if (!normalizedToken) {
      setValidationError('A runtime API token is required.');
      return;
    }

    if (!isAllowedApiUrl(normalizedUrl)) {
      setValidationError(
        import.meta.env.DEV
          ? 'Enter a valid HTTP or HTTPS Worker URL, or leave it blank for the local proxy.'
          : 'Enter a valid HTTPS Worker URL. Runtime tokens are never sent over cleartext HTTP.'
      );
      return;
    }

    try {
      saveRuntimeApiConfig({ apiUrl: normalizedUrl, token: normalizedToken });
    } catch {
      setValidationError('Session storage is unavailable. Enable it for this site and try again.');
      return;
    }
    setConfigured(true);
    setEditing(false);
    setToken('');
    setValidationError('');
  };

  if (!configured || editing) {
    return (
      <main className="main-content" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <div className="panel panel-pad" style={{ width: 'min(520px, 100%)' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Connect to the Shipping Worker</h1>
          <p style={{ color: 'var(--aged-mahogany-light)', marginBottom: '24px' }}>
            Connection details are kept only in this browser tab. The token is never included in the app build.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="runtime-api-url">Worker API URL *</label>
              <input
                id="runtime-api-url"
                type="url"
                value={apiUrl}
                onChange={(event) => setApiUrl(event.target.value)}
                placeholder={import.meta.env.DEV ? 'Same-origin local proxy' : 'https://your-worker.workers.dev'}
                required={!import.meta.env.DEV}
                autoComplete="url"
              />
              {import.meta.env.DEV && (
                <p className="field-hint">Leave blank to use the Vite same-origin proxy.</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="runtime-api-token">Runtime Token *</label>
              <input
                id="runtime-api-token"
                type="password"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                required
                autoComplete="off"
              />
            </div>

            {validationError && (
              <div role="alert" style={{ color: 'var(--error-text)', marginBottom: '16px' }}>
                {validationError}
              </div>
            )}

            <div className="action-buttons">
              <button type="submit" className="btn btn-primary">
                Connect
              </button>
              {configured && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setEditing(false);
                    setToken('');
                    setValidationError('');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
    );
  }

  const connectedUrl = getRuntimeApiConfig()?.apiUrl ?? '';

  return (
    <SessionActionProvider
      value={{
        onAction: () => {
          const current = getRuntimeApiConfig();
          setApiUrl(current?.apiUrl ?? getDefaultApiUrl());
          // Never copy an existing bearer token back into an editable field.
          // Re-authentication requires deliberate re-entry.
          setToken('');
          setEditing(true);
        },
        actionLabel: 'Edit API connection',
        actionTitle: billableLocked
          ? 'Connection settings are locked while a billable request is processing.'
          : 'Edit API connection',
        disabled: billableLocked,
        userName: 'Worker session',
        userEmail: connectedUrl ? hostLabel(connectedUrl) : undefined,
      }}
    >
      {children}
    </SessionActionProvider>
  );
}

export default TokenGate;
